import { Fragment, useRef, useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { useDebounced } from '@/api/useDebounced';
import { LoadingState, EmptyState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { StatusBadge, statusLabel } from '@/components/StatusBadge';
import { RequirePermission, PermissionDenied } from '@/components/RequirePermission';
import { useAuth } from '@/auth/AuthContext';
import { useLanguage, useT, type TranslateFn } from '@/i18n/LanguageContext';
import { formatCurrencyMinor, formatDate, formatDateTime, formatNumber } from '@/i18n/format';
import type { TranslationKey } from '@/i18n/dictionaries/en';

/**
 * An invoice line's `description` is backend-generated at issue time as
 * `kind` or `kind (doc_kind doc_id)` (packages/billing/src/services/
 * invoices.ts) and frozen forever once the invoice leaves DRAFT - a
 * display-only fix here, same as elsewhere, must never touch that stored
 * text. Only the leading raw enum token gets humanized (via the exact same
 * statusLabel/LABEL_KEYS lookup the Charges tab already uses); any
 * `(doc_kind doc_id)` technical reference that follows is left exactly as
 * stored, matching the established "audit/technical identifiers stay raw"
 * convention.
 */
function humanizeLineDescription(t: TranslateFn, raw: string): string {
  const match = /^([A-Z][A-Z_]*)/.exec(raw);
  if (!match) return raw;
  return statusLabel(t, match[0]) + raw.slice(match[0].length);
}

const TAB_KEYS = ['overview', 'contracts', 'tariffs', 'charges', 'invoices', 'manualService', 'legalEntitySettings', 'dataExport'] as const;
type TabKey = (typeof TAB_KEYS)[number];
const TAB_LABEL_KEYS: Record<TabKey, TranslationKey> = {
  overview: 'billing.tabOverview',
  contracts: 'billing.tabContracts',
  tariffs: 'billing.tabTariffs',
  charges: 'billing.tabCharges',
  invoices: 'billing.tabInvoices',
  manualService: 'billing.tabManualService',
  legalEntitySettings: 'billing.tabLegalEntitySettings',
  dataExport: 'billing.tabDataExport',
};

/**
 * Billing (Part B, "one of the most important sections"). No complex P&L -
 * just: charges this period, unpriced billable events, manual services,
 * seller totals, contracts without a tariff. The tariff editor never
 * rewrites history - setTariffRate always closes the old rule and opens a
 * new one (server-enforced); this UI never offers an "edit in place" for a
 * past rate.
 */
export function BillingPage(): JSX.Element {
  const { hasPermission } = useAuth();
  const t = useT();
  const [tab, setTab] = useState<TabKey>('overview');

  if (!hasPermission('BILLING.READ')) {
    return <PermissionDenied reason={t('billing.permissionDenied')} />;
  }

  return (
    <div>
      <div className="page-header">
        <h1>{t('nav.billing')}</h1>
      </div>
      <div className="tabs">
        {TAB_KEYS.map((key) => (
          <button key={key} type="button" className={tab === key ? 'active' : ''} onClick={() => setTab(key)}>
            {t(TAB_LABEL_KEYS[key])}
          </button>
        ))}
      </div>
      {tab === 'overview' && <BillingOverviewTab />}
      {tab === 'contracts' && <ContractsTab />}
      {tab === 'tariffs' && <TariffsTab />}
      {tab === 'charges' && <ChargesTab />}
      {tab === 'invoices' && <InvoicesTab />}
      {tab === 'manualService' && (
        <RequirePermission permission="BILLING.WRITE" fallback={<PermissionDenied reason={t('billing.manualServicePermissionDenied')} />}>
          <ManualServiceForm />
        </RequirePermission>
      )}
      {tab === 'legalEntitySettings' && <LegalEntitySettingsTab />}
      {tab === 'dataExport' && (
        <RequirePermission permission="EXPORT.READ" fallback={<PermissionDenied reason={t('billing.dataExportPermissionDenied')} />}>
          <DataExportTab />
        </RequirePermission>
      )}
    </div>
  );
}

function BillingOverviewTab(): JSX.Element {
  const summary = useApi(() => endpoints.billingSummary(), []);
  const unbilled = useApi(() => endpoints.billingUnbilled(), []);
  const { t, locale } = useLanguage();

  return (
    <div>
      <div className="grid-cards">
        <div className="stat-card">
          <div className="n">{summary.status === 'success' ? formatNumber((summary.data as { rows?: unknown[] }).rows?.length ?? 0, locale) : '…'}</div>
          <div className="label">{t('billing.sellerServiceBreakdowns')}</div>
        </div>
        <div className="stat-card">
          <div className="n">{unbilled.status === 'success' ? formatNumber((unbilled.data as { rows?: unknown[] }).rows?.length ?? 0, locale) : '…'}</div>
          <div className="label">{t('billing.unpricedBillableEvents')}</div>
        </div>
      </div>
      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('billing.operationsWithNoCharge')}</h2>
        {unbilled.status === 'loading' && <LoadingState />}
        {unbilled.status === 'error' && <ErrorBanner error={unbilled.error} onRetry={unbilled.reload} />}
        {unbilled.status === 'success' &&
          (() => {
            const rows = ((unbilled.data as Record<string, unknown>).rows ?? unbilled.data) as Record<string, unknown>[];
            if (!Array.isArray(rows) || rows.length === 0) return <EmptyState title={t('billing.everyOperationCharged')} />;
            return (
              <div className="table-scroll"><table>
                <thead>
                  <tr>
                    <th>{t('common.reason')}</th>
                    <th>{t('inboundDetail.colQty')}</th>
                    <th>{t('inventoryDetail.colOccurred')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i}>
                      <td>
                        <StatusBadge value={String(row.reason)} />
                      </td>
                      <td>{formatNumber(String(row.qty), locale)}</td>
                      <td>{formatDateTime(String(row.occurred_at), locale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            );
          })()}
      </div>
    </div>
  );
}

function ContractsTab(): JSX.Element {
  const result = useApi(() => endpoints.billingContracts(), []);
  const { t, locale } = useLanguage();
  if (result.status === 'loading') return <LoadingState />;
  if (result.status === 'error') return <ErrorBanner error={result.error} onRetry={result.reload} />;
  if (result.data.contracts.length === 0) return <EmptyState title={t('billing.noContractsYet')} />;
  return (
    <div className="table-scroll"><table>
      <thead>
        <tr>
          <th>{t('inbounds.colSeller')}</th>
          <th>{t('common.status')}</th>
          <th>{t('billing.tariff')}</th>
          <th>{t('billing.currency')}</th>
          <th>{t('billing.effectiveFrom')}</th>
        </tr>
      </thead>
      <tbody>
        {result.data.contracts.map((c, i) => {
          const row = c as Record<string, unknown>;
          return (
            <tr key={i}>
              <td>{row.owner_name ? String(row.owner_name) : String(row.owner_id ?? '—')}</td>
              <td>
                <StatusBadge value={String(row.state)} />
              </td>
              <td>{row.tariff_name ? String(row.tariff_name) : t('common.none')}</td>
              <td>{String(row.currency)}</td>
              <td>{row.valid_from ? formatDate(String(row.valid_from), locale) : '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table></div>
  );
}

function TariffsTab(): JSX.Element {
  const [tariffId, setTariffId] = useState('');
  // Chosen by NAME from the tariff list - an operator never pastes a tariff id.
  const tariffs = useApi(() => endpoints.billingTariffs(), []);
  const rules = useApi(() => (tariffId ? endpoints.tariffRules(tariffId) : Promise.resolve({ rules: [] })), [tariffId]);
  const { hasPermission } = useAuth();
  const { t, locale } = useLanguage();
  const [code, setCode] = useState('');
  const [rate, setRate] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);

  async function submitRate(): Promise<void> {
    setSaving(true);
    setSaveError(null);
    try {
      await endpoints.setTariffRate({ tariffId, code, rate });
      setCode('');
      setRate('');
      rules.reload();
    } catch (err) {
      setSaveError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="field-row">
        <label htmlFor="tariff-id">{t('billing.tariff')}</label>
        <select id="tariff-id" value={tariffId} onChange={(e) => setTariffId(e.target.value)} disabled={tariffs.status !== 'success'}>
          <option value="">{t('billing.selectTariff')}</option>
          {tariffs.status === 'success' &&
            tariffs.data.tariffs.map((tf) => (
              <option key={tf.id} value={tf.id}>
                {tf.name} ({tf.currency})
              </option>
            ))}
        </select>
      </div>
      {tariffs.status === 'error' && <ErrorBanner error={tariffs.error} onRetry={tariffs.reload} />}
      {tariffs.status === 'success' && tariffs.data.tariffs.length === 0 && <EmptyState title={t('billing.noTariffsYet')} />}

      {tariffId && rules.status === 'loading' && <LoadingState />}
      {tariffId && rules.status === 'error' && <ErrorBanner error={rules.error} onRetry={rules.reload} />}
      {tariffId && rules.status === 'success' && (
        <div className="table-scroll"><table>
          <thead>
            <tr>
              <th>{t('billing.colCode')}</th>
              <th>{t('billing.colKind')}</th>
              <th>{t('billing.colRate')}</th>
              <th>{t('billing.colUnit')}</th>
              <th>{t('billing.effectiveFrom')}</th>
            </tr>
          </thead>
          <tbody>
            {rules.data.rules.map((r, i) => {
              const row = r as Record<string, unknown>;
              return (
                <tr key={i}>
                  <td>{String(row.code)}</td>
                  <td>{statusLabel(t, String(row.kind))}</td>
                  <td>{formatNumber(String(row.rate), locale)}</td>
                  <td>{String(row.unit)}</td>
                  <td>{row.effective_at ? formatDate(String(row.effective_at), locale) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
      )}

      {tariffId && hasPermission('BILLING.WRITE') && (
        <div className="card">
          <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('billing.priceAService')}</h2>
          <p className="sub">{t('billing.priceAServiceHint')}</p>
          <div className="field-row">
            <label htmlFor="rate-code">{t('billing.serviceCode')}</label>
            <input id="rate-code" value={code} onChange={(e) => setCode(e.target.value)} />
          </div>
          <div className="field-row">
            <label htmlFor="rate-value">{t('billing.colRate')}</label>
            <input id="rate-value" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="12.50" />
          </div>
          {Boolean(saveError) && <ErrorBanner error={saveError} />}
          <div className="form-actions">
            <button type="button" className="primary" disabled={saving || !code || !rate} onClick={() => void submitRate()}>
              {saving ? t('common.saving') : t('billing.saveNewRate')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ChargesTab(): JSX.Element {
  const result = useApi(() => endpoints.billingCharges(), []);
  const [openId, setOpenId] = useState<string | null>(null);
  const { t, locale } = useLanguage();

  if (result.status === 'loading') return <LoadingState />;
  if (result.status === 'error') return <ErrorBanner error={result.error} onRetry={result.reload} />;
  if (result.data.charges.length === 0) return <EmptyState title={t('billing.noChargesYet')} />;

  return (
    <div className="table-scroll"><table>
      <thead>
        <tr>
          <th>{t('billing.colDate')}</th>
          <th>{t('billing.colKind')}</th>
          <th>{t('inboundDetail.colQty')}</th>
          <th>{t('billing.colRate')}</th>
          <th>{t('billing.colAmount')}</th>
          <th>{t('billing.colSource')}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {result.data.charges.map((c) => {
          const row = c as Record<string, unknown>;
          const id = String(row.id);
          return (
            <Fragment key={id}>
              <tr onClick={() => setOpenId(openId === id ? null : id)}>
                <td>{formatDate(String(row.occurred_at), locale)}</td>
                <td>{statusLabel(t, String(row.kind))}</td>
                <td>{formatNumber(String(row.qty), locale)}</td>
                <td>{formatNumber(String(row.rate), locale)}</td>
                <td>
                  {formatCurrencyMinor(String(row.amount_minor), String(row.currency), locale)}
                  {row.is_manual ? <span className="badge neutral" style={{ marginLeft: 6 }}>{t('billing.manual')}</span> : null}
                </td>
                <td>{row.source_kind ? String(row.source_kind) : '—'}</td>
                <td>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setOpenId(openId === id ? null : id); }}>
                    {openId === id ? t('billing.hideEvidence') : t('billing.chargeEvidence')}
                  </button>
                </td>
              </tr>
              {openId === id && (
                <tr>
                  <td colSpan={7}>
                    <dl className="kv">
                      <dt>{t('billing.physicalOperation')}</dt>
                      <dd>{row.source_kind ? `${String(row.source_kind)} / ${String(row.source_id)}` : t('billing.noneManualEntry')}</dd>
                      <dt>{t('billing.businessDocument')}</dt>
                      <dd>{row.doc_kind ? `${String(row.doc_kind)} / ${String(row.doc_id)}` : '—'}</dd>
                      <dt>{t('billing.actorDevice')}</dt>
                      <dd>{row.actor_membership_id ? String(row.actor_membership_id) : row.device_id ? String(row.device_id) : '—'}</dd>
                      <dt>{t('billing.tariffRuleSnapshot')}</dt>
                      <dd>{row.tariff_rule_id ? String(row.tariff_rule_id) : t('billing.noTariffRuleApplied')}</dd>
                      <dt>{t('billing.calculation')}</dt>
                      <dd>
                        {formatNumber(String(row.qty), locale)} {String(row.unit)} × {formatNumber(String(row.rate), locale)} {String(row.currency)} ={' '}
                        {formatCurrencyMinor(String(row.amount_minor), String(row.currency), locale)}
                      </dd>
                      {row.note ? (
                        <>
                          <dt>{t('billing.reasonNote')}</dt>
                          <dd>{String(row.note)}</dd>
                        </>
                      ) : null}
                    </dl>
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table></div>
  );
}

function ManualServiceForm(): JSX.Element {
  const [ownerId, setOwnerId] = useState('');
  const [code, setCode] = useState('');
  const [qty, setQty] = useState('');
  const [rate, setRate] = useState('');
  const [reason, setReason] = useState('');
  const [docKind, setDocKind] = useState<'' | 'SALES_ORDER' | 'INBOUND_SHIPMENT'>('');
  const [docId, setDocId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState(false);
  const t = useT();
  // P1: the seller is CHOSEN from the sellers list - an operator never types an owner uuid.
  const sellers = useApi(() => endpoints.sellers({ activeOnly: false, limit: 200 }), []);
  const sellerOptions = sellers.status === 'success' ? sellers.data.rows : [];
  // The reference document is chosen by its NUMBER from this seller's own
  // orders / inbounds (BILLING.READ lookup); the same doc_kind + doc_id pair
  // the API always took is still what is sent.
  const [docSearch, setDocSearch] = useState('');
  const docQuery = useDebounced(docSearch.trim(), 250);
  const documents = useApi(
    () =>
      ownerId && docKind
        ? endpoints.billingDocuments({ ownerId, kind: docKind, q: docQuery || undefined, limit: 100 })
        : Promise.resolve({ rows: [] as readonly { id: string; number: string; state: string; created_at: string }[] }),
    [ownerId, docKind, docQuery],
  );

  // One key per submission: a double click or a retry after a timeout re-sends the SAME key and the server returns the
  // first result instead of booking a second charge. A new key is made after a success, so a real next charge is its own.
  const submitKey = useRef<string>(crypto.randomUUID());

  async function submit(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      await endpoints.recordManualService({
        ownerId,
        code,
        qty,
        reason,
        docKind: docKind && docId ? docKind : null,
        docId: docKind && docId ? docId : null,
        idempotencyKey: submitKey.current,
      });
      submitKey.current = crypto.randomUUID();
      setDone(true);
      setOwnerId('');
      setCode('');
      setQty('');
      setRate('');
      setReason('');
      setDocKind('');
      setDocId('');
      setDocSearch('');
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{ maxWidth: 480 }}>
      <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('billing.newManualService')}</h2>
      <p className="sub">{t('billing.newManualServiceHint')}</p>
      {done && <div className="badge ok" style={{ marginBottom: 12 }}>{t('common.recorded')}</div>}
      {Boolean(error) && <ErrorBanner error={error} />}
      <div className="field-row">
        <label htmlFor="ms-owner">{t('billing.sellerOwnerId')}</label>
        <select
          id="ms-owner"
          value={ownerId}
          onChange={(e) => {
            setOwnerId(e.target.value);
            setDocId('');
          }}
          required
        >
          <option value="">{t('billing.selectSeller')}</option>
          {sellerOptions.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
      <div className="field-row">
        <label htmlFor="ms-code">{t('billing.serviceCode')}</label>
        <input id="ms-code" value={code} onChange={(e) => setCode(e.target.value)} required />
      </div>
      <div className="field-row">
        <label htmlFor="ms-qty">{t('billing.quantity')}</label>
        <input id="ms-qty" value={qty} onChange={(e) => setQty(e.target.value)} required />
      </div>
      <div className="field-row">
        <label htmlFor="ms-rate">{t('billing.rateInfoOnly')}</label>
        <input id="ms-rate" value={rate} onChange={(e) => setRate(e.target.value)} />
      </div>
      <div className="field-row">
        <label htmlFor="ms-reason">{t('billing.reasonRequired')}</label>
        <textarea id="ms-reason" value={reason} onChange={(e) => setReason(e.target.value)} required />
      </div>
      <div className="field-row">
        <label htmlFor="ms-dockind">{t('billing.referenceDocKind')}</label>
        <select
          id="ms-dockind"
          value={docKind}
          onChange={(e) => {
            setDocKind(e.target.value as '' | 'SALES_ORDER' | 'INBOUND_SHIPMENT');
            setDocId('');
            setDocSearch('');
          }}
        >
          <option value="">{t('billing.docKindNone')}</option>
          <option value="SALES_ORDER">{t('billing.docKindOrder')}</option>
          <option value="INBOUND_SHIPMENT">{t('billing.docKindInbound')}</option>
        </select>
      </div>
      {docKind && (
        <div className="field-row">
          <label htmlFor="ms-docid">{t('billing.referenceDocument')}</label>
          {ownerId && (
            <input
              type="search"
              aria-label={t('billing.searchDocumentNumber')}
              placeholder={t('billing.searchDocumentNumber')}
              value={docSearch}
              onChange={(e) => setDocSearch(e.target.value)}
            />
          )}
          <select id="ms-docid" value={docId} onChange={(e) => setDocId(e.target.value)} disabled={!ownerId || documents.status !== 'success'}>
            <option value="">{ownerId ? t('billing.selectDocument') : t('billing.selectSellerFirst')}</option>
            {documents.status === 'success' &&
              documents.data.rows.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.number} · {statusLabel(t, d.state)}
                </option>
              ))}
          </select>
          {documents.status === 'error' && <ErrorBanner error={documents.error} onRetry={documents.reload} />}
          {ownerId && documents.status === 'success' && documents.data.rows.length === 0 && (
            <span className="disabled-note">{t('billing.noDocumentsForSeller')}</span>
          )}
        </div>
      )}
      <div className="form-actions">
        <button type="button" className="primary" disabled={saving || !ownerId || !code || !qty || !reason} onClick={() => void submit()}>
          {saving ? t('billing.recording') : t('billing.recordCharge')}
        </button>
      </div>
    </div>
  );
}

/**
 * Invoices (COMMERCIAL LAYER epic, ADR-075). List with seller/status/period
 * filters, a detail drill-down (line-level, each line traceable back to its
 * source billable_event id - the exact evidence trail ChargesTab's own
 * drill-down already shows for charges), generate/issue/void/payment/adjust
 * actions gated the same way ManualServiceForm already is (BILLING.WRITE),
 * and a PDF download. No payment gateway exists in this epic - "record
 * payment" is a manual reconciliation entry, never a live charge.
 */
function InvoicesTab(): JSX.Element {
  const [status, setStatus] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const list = useApi(
    () => endpoints.invoices({ ...(status ? { status } : {}), ...(ownerId ? { ownerId } : {}) }),
    [status, ownerId],
  );
  const sellers = useApi(() => endpoints.sellers({ activeOnly: false, limit: 200 }), []);
  const sellerOptions = sellers.status === 'success' ? sellers.data.rows : [];
  const { hasPermission } = useAuth();
  const { t, locale } = useLanguage();

  return (
    <div>
      <div className="field-row" style={{ display: 'flex', gap: 12 }}>
        <div>
          <label htmlFor="inv-owner">{t('inbounds.colSeller')}</label>
          <select id="inv-owner" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
            <option value="">{t('billing.allSellers')}</option>
            {sellerOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="inv-status">{t('common.status')}</label>
          <select id="inv-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">{t('inbounds.allStatuses')}</option>
            <option value="DRAFT">{t('status.DRAFT')}</option>
            <option value="ISSUED">{t('billing.statusIssued')}</option>
            <option value="PARTIALLY_PAID">{t('billing.statusPartiallyPaid')}</option>
            <option value="PAID">{t('billing.statusPaid')}</option>
            <option value="OVERDUE">{t('status.overdue')}</option>
            <option value="VOID">{t('billing.statusVoid')}</option>
          </select>
        </div>
      </div>

      {hasPermission('BILLING.WRITE') && <GenerateInvoiceForm onGenerated={() => list.reload()} />}

      {list.status === 'loading' && <LoadingState />}
      {list.status === 'error' && <ErrorBanner error={list.error} onRetry={list.reload} />}
      {list.status === 'success' && list.data.rows.length === 0 && <EmptyState title={t('billing.noInvoicesMatch')} />}
      {list.status === 'success' && list.data.rows.length > 0 && (
        <div className="table-scroll"><table>
          <thead>
            <tr>
              <th>{t('billing.colNumber')}</th>
              <th>{t('inbounds.colSeller')}</th>
              <th>{t('billing.colPeriod')}</th>
              <th>{t('common.status')}</th>
              <th>{t('common.total')}</th>
              <th>{t('orders.colDue')}</th>
            </tr>
          </thead>
          <tbody>
            {list.data.rows.map((r) => {
              const row = r as Record<string, unknown>;
              const id = String(row.id);
              return (
                <Fragment key={id}>
                  <tr onClick={() => setOpenId(openId === id ? null : id)}>
                    <td>{row.number ? String(row.number) : <span className="badge neutral">{t('status.DRAFT')}</span>}</td>
                    <td>{row.owner_name ? String(row.owner_name) : String(row.owner_id)}</td>
                    <td>
                      {formatDate(String(row.period_start), locale)} – {formatDate(String(row.period_end), locale)}
                    </td>
                    <td>
                      {/* the server's derived status (balance with credit/debit notes, overdue), not the stored legal state */}
                      <StatusBadge value={String(row.displayStatus ?? row.state)} />
                    </td>
                    <td>{formatCurrencyMinor(String(row.total_minor), String(row.currency), locale)}</td>
                    <td>{row.due_at ? formatDate(String(row.due_at), locale) : '—'}</td>
                  </tr>
                  {openId === id && (
                    <tr>
                      <td colSpan={6}>
                        <InvoiceDetailPanel invoiceId={id} onChanged={() => list.reload()} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table></div>
      )}
    </div>
  );
}

function GenerateInvoiceForm({ onGenerated }: { onGenerated: () => void }): JSX.Element {
  const [contractId, setContractId] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const contracts = useApi(() => endpoints.billingContracts(), []);
  const contractOptions = contracts.status === 'success' ? contracts.data.contracts : [];
  const t = useT();

  async function submit(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      await endpoints.generateInvoice({ contractId, periodStart, periodEnd });
      setContractId('');
      onGenerated();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{ maxWidth: 560, marginBottom: 16 }}>
      <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('billing.generateDraftInvoice')}</h2>
      <p className="sub">{t('billing.generateDraftInvoiceHint')}</p>
      {Boolean(error) && <ErrorBanner error={error} />}
      <div className="field-row">
        <label htmlFor="gen-contract">{t('billing.contract')}</label>
        <select id="gen-contract" value={contractId} onChange={(e) => setContractId(e.target.value)} required>
          <option value="">{t('billing.selectContract')}</option>
          {contractOptions.map((c, i) => {
            const row = c as Record<string, unknown>;
            const id = String(row.id ?? '');
            return (
              <option key={id || i} value={id}>
                {row.owner_name ? String(row.owner_name) : String(row.owner_id ?? id)}
                {row.tariff_name ? ` — ${String(row.tariff_name)}` : ''} ({String(row.currency)})
              </option>
            );
          })}
        </select>
      </div>
      <div className="field-row">
        <label htmlFor="gen-start">{t('billing.periodStart')}</label>
        <input id="gen-start" type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} required />
      </div>
      <div className="field-row">
        <label htmlFor="gen-end">{t('billing.periodEnd')}</label>
        <input id="gen-end" type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} required />
      </div>
      <div className="form-actions">
        <button
          type="button"
          className="primary"
          disabled={saving || !contractId || !periodStart || !periodEnd}
          onClick={() => void submit()}
        >
          {saving ? t('billing.generating') : t('billing.generateRegenerateDraft')}
        </button>
      </div>
    </div>
  );
}

function InvoiceDetailPanel({ invoiceId, onChanged }: { invoiceId: string; onChanged: () => void }): JSX.Element {
  const detail = useApi(() => endpoints.invoiceDetail(invoiceId), [invoiceId]);
  const { hasPermission } = useAuth();
  const { t, locale } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [voidReason, setVoidReason] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const payKey = useRef<string>(crypto.randomUUID());
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustKind, setAdjustKind] = useState<'CREDIT_NOTE' | 'DEBIT_NOTE'>('CREDIT_NOTE');

  if (detail.status === 'loading') return <LoadingState />;
  if (detail.status === 'error') return <ErrorBanner error={detail.error} onRetry={detail.reload} />;
  const data = detail.data as Record<string, unknown>;
  const invoice = data.invoice as Record<string, unknown>;
  const lines = (data.lines ?? []) as Record<string, unknown>[];
  const currency = String(invoice.currency);
  const canWrite = hasPermission('BILLING.WRITE');

  async function run(fn: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await fn();
      detail.reload();
      onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function downloadPdf(): Promise<void> {
    const { bytesBase64 } = await endpoints.invoicePdf(invoiceId);
    const a = document.createElement('a');
    a.href = `data:application/pdf;base64,${bytesBase64}`;
    a.download = `${invoice.number ? String(invoice.number) : invoiceId}.pdf`;
    a.click();
  }

  return (
    <div className="card">
      {Boolean(error) && <ErrorBanner error={error} />}
      <dl className="kv">
        <dt>{t('billing.subtotal')}</dt>
        <dd>{formatCurrencyMinor(String(invoice.subtotal_minor), currency, locale)}</dd>
        <dt>{t('billing.tax')}</dt>
        <dd>{formatCurrencyMinor(String(invoice.tax_minor), currency, locale)}</dd>
        <dt>{t('common.total')}</dt>
        <dd>{formatCurrencyMinor(String(invoice.total_minor), currency, locale)}</dd>
        <dt>{t('billing.paid')}</dt>
        <dd>{formatCurrencyMinor(String(invoice.paid_minor), currency, locale)}</dd>
        <dt>{t('billing.outstandingBalance')}</dt>
        <dd>{formatCurrencyMinor(String(data.balanceMinor), currency, locale)}</dd>
      </dl>

      <h3 style={{ fontSize: 13 }}>{t('billing.linesTraceable')}</h3>
      <div className="table-scroll"><table>
        <thead>
          <tr>
            <th>{t('billing.colKind')}</th>
            <th>{t('billing.description')}</th>
            <th>{t('inboundDetail.colQty')}</th>
            <th>{t('billing.colRate')}</th>
            <th>{t('billing.colAmount')}</th>
            <th>{t('billing.sourceCharge')}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i}>
              <td>{statusLabel(t, String(l.kind))}</td>
              <td>{humanizeLineDescription(t, String(l.description))}</td>
              <td>{formatNumber(String(l.qty), locale)}</td>
              <td>{formatNumber(String(l.rate), locale)}</td>
              <td>{formatCurrencyMinor(String(l.amount_minor), String(l.currency), locale)}</td>
              <td>{l.source_billable_event_id ? String(l.source_billable_event_id) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table></div>

      <div className="form-actions" style={{ marginTop: 12, flexWrap: 'wrap', gap: 8 }}>
        <button type="button" onClick={() => void downloadPdf()}>{t('billing.downloadPdf')}</button>
        {canWrite && invoice.state === 'DRAFT' && (
          <button type="button" className="primary" disabled={busy} onClick={() => void run(() => endpoints.issueInvoice({ invoiceId }))}>
            {t('billing.issue')}
          </button>
        )}
      </div>

      {canWrite && invoice.state !== 'VOID' && (
        <div className="field-row" style={{ marginTop: 12 }}>
          <label htmlFor={`void-reason-${invoiceId}`}>{t('billing.voidReason')}</label>
          <input id={`void-reason-${invoiceId}`} value={voidReason} onChange={(e) => setVoidReason(e.target.value)} />
          <button
            type="button"
            disabled={busy || !voidReason}
            onClick={() => void run(() => endpoints.voidInvoice({ invoiceId, reason: voidReason }))}
          >
            {t('billing.void')}
          </button>
        </div>
      )}

      {canWrite && invoice.state !== 'DRAFT' && invoice.state !== 'VOID' && BigInt(String(data.balanceMinor)) > 0n && (
        <div className="field-row" style={{ marginTop: 12 }}>
          <label htmlFor={`pay-${invoiceId}`}>{t('billing.recordPaymentLabel')}</label>
          <input id={`pay-${invoiceId}`} value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} placeholder={t('billing.amountInMinorUnits')} />
          <button
            type="button"
            disabled={busy || !paymentAmount}
            onClick={() =>
              void run(async () => {
                // same key on a repeated click / retry of this payment; fresh key once it has been recorded
                await endpoints.recordInvoicePayment({ invoiceId, amountMinor: paymentAmount, currency, method: 'BANK_TRANSFER', idempotencyKey: payKey.current });
                payKey.current = crypto.randomUUID();
              })
            }
          >
            {t('billing.recordPayment')}
          </button>
        </div>
      )}

      {canWrite && invoice.state !== 'DRAFT' && invoice.state !== 'VOID' && (
        <div className="field-row" style={{ marginTop: 12, flexDirection: 'column', alignItems: 'flex-start' }}>
          <label>{t('billing.adjustmentLabel')}</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <select value={adjustKind} onChange={(e) => setAdjustKind(e.target.value as 'CREDIT_NOTE' | 'DEBIT_NOTE')}>
              <option value="CREDIT_NOTE">{t('billing.creditNote')}</option>
              <option value="DEBIT_NOTE">{t('billing.debitNote')}</option>
            </select>
            <input value={adjustAmount} onChange={(e) => setAdjustAmount(e.target.value)} placeholder={t('billing.amountInMinorUnits')} />
            <input value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} placeholder={t('billing.reasonRequiredPlaceholder')} />
            <button
              type="button"
              disabled={busy || !adjustAmount || !adjustReason}
              onClick={() =>
                void run(() =>
                  endpoints.adjustInvoice({ invoiceId, kind: adjustKind, amountMinor: adjustAmount, currency, reason: adjustReason }),
                )
              }
            >
              {t('billing.apply')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ========================================================================== */
/*  Legal Entity Settings (ADR-078 Parts A/B) - tax/VAT config + invoice     */
/*  legal template. Both append-only: there is no "edit" here, only "save a  */
/*  new version" - the server never overwrites a prior row, same discipline  */
/*  the Tariffs tab already uses for rate changes.                           */
/* ========================================================================== */

function LegalEntitySettingsTab(): JSX.Element {
  const [legalEntityId, setLegalEntityId] = useState('');
  const legalEntities = useApi(() => endpoints.legalEntities(), []);
  const options = legalEntities.status === 'success' ? legalEntities.data.legalEntities : [];
  const t = useT();

  return (
    <div>
      <div className="field-row">
        <label htmlFor="le-settings-id">{t('billing.legalEntity')}</label>
        <select id="le-settings-id" value={legalEntityId} onChange={(e) => setLegalEntityId(e.target.value)}>
          <option value="">{t('billing.selectLegalEntity')}</option>
          {options.map((le) => (
            <option key={le.id} value={le.id}>
              {le.name} ({le.country})
            </option>
          ))}
        </select>
        {options.length === 0 && legalEntities.status === 'success' && (
          <p className="sub">{t('billing.noLegalEntitiesYet')}</p>
        )}
      </div>
      {legalEntityId && (
        <>
          <TaxConfigSection legalEntityId={legalEntityId} />
          <InvoiceTemplateSection legalEntityId={legalEntityId} />
        </>
      )}
    </div>
  );
}

function TaxConfigSection({ legalEntityId }: { legalEntityId: string }): JSX.Element {
  const result = useApi(() => endpoints.taxConfig(legalEntityId), [legalEntityId]);
  const { hasPermission } = useAuth();
  const { t, locale } = useLanguage();
  const [jurisdiction, setJurisdiction] = useState('');
  const [isExempt, setIsExempt] = useState(false);
  const [rate, setRate] = useState('');
  const [taxIdNumber, setTaxIdNumber] = useState('');
  const [label, setLabel] = useState('VAT');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);

  async function submit(): Promise<void> {
    setSaving(true);
    setSaveError(null);
    try {
      await endpoints.saveTaxConfig({
        legalEntityId,
        jurisdiction,
        isExempt,
        rate: isExempt ? undefined : rate,
        taxIdNumber: taxIdNumber || undefined,
        label: label || undefined,
        effectiveFrom: new Date().toISOString(),
      });
      setJurisdiction('');
      setRate('');
      setTaxIdNumber('');
      result.reload();
    } catch (err) {
      setSaveError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card">
      <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('billing.taxVatConfig')}</h2>
      <p className="sub">{t('billing.taxVatConfigHint')}</p>
      {result.status === 'loading' && <LoadingState />}
      {result.status === 'error' && <ErrorBanner error={result.error} onRetry={result.reload} />}
      {result.status === 'success' && (
        <>
          {result.data.active ? (
            <dl className="kv">
              <dt>{t('billing.currentlyActive')}</dt>
              <dd>
                {String(result.data.active['jurisdiction'])} —{' '}
                {result.data.active['is_exempt'] ? t('billing.exempt') : `${String(result.data.active['label'])} ${String(result.data.active['rate'])}`}
                {result.data.active['tax_id_number'] ? ` (${String(result.data.active['tax_id_number'])})` : ''}
              </dd>
            </dl>
          ) : (
            <EmptyState title={t('billing.noTaxConfigOnFile')} />
          )}
          {result.data.history.length > 0 && (
            <div className="table-scroll"><table>
              <thead>
                <tr>
                  <th>{t('billing.jurisdiction')}</th>
                  <th>{t('billing.colRate')}</th>
                  <th>{t('billing.effectiveFrom')}</th>
                  <th>{t('billing.active')}</th>
                </tr>
              </thead>
              <tbody>
                {result.data.history.map((row, i) => {
                  const r = row as Record<string, unknown>;
                  return (
                    <tr key={i}>
                      <td>{String(r.jurisdiction)}</td>
                      <td>{r.is_exempt ? t('billing.exempt') : formatNumber(String(r.rate), locale)}</td>
                      <td>{formatDate(String(r.effective_from), locale)}</td>
                      <td>{r.is_active ? t('common.yes') : t('common.no')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          )}
        </>
      )}

      {hasPermission('BILLING.WRITE') && (
        <div style={{ marginTop: 16 }}>
          <h3 style={{ fontSize: 13 }}>{t('billing.addNewVersion')}</h3>
          <div className="field-row">
            <label htmlFor="tax-jurisdiction">{t('billing.jurisdictionIso')}</label>
            <input id="tax-jurisdiction" value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value.toUpperCase())} maxLength={2} />
          </div>
          <div className="field-row">
            <label htmlFor="tax-exempt">
              <input id="tax-exempt" type="checkbox" checked={isExempt} onChange={(e) => setIsExempt(e.target.checked)} /> {t('billing.exemptNoVat')}
            </label>
          </div>
          {!isExempt && (
            <div className="field-row">
              <label htmlFor="tax-rate">{t('billing.rateFraction')}</label>
              <input id="tax-rate" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="0.19" />
            </div>
          )}
          <div className="field-row">
            <label htmlFor="tax-id-number">{t('billing.taxVatRegNumber')}</label>
            <input id="tax-id-number" value={taxIdNumber} onChange={(e) => setTaxIdNumber(e.target.value)} />
          </div>
          <div className="field-row">
            <label htmlFor="tax-label">{t('billing.label')}</label>
            <input id="tax-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="VAT / GST / NDS…" />
          </div>
          {Boolean(saveError) && <ErrorBanner error={saveError} />}
          <div className="form-actions">
            <button type="button" className="primary" disabled={saving || !jurisdiction || (!isExempt && !rate)} onClick={() => void submit()}>
              {saving ? t('common.saving') : t('billing.saveNewTaxConfig')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function InvoiceTemplateSection({ legalEntityId }: { legalEntityId: string }): JSX.Element {
  const result = useApi(() => endpoints.invoiceTemplate(legalEntityId), [legalEntityId]);
  const { hasPermission } = useAuth();
  const t = useT();
  const [sellerLegalName, setSellerLegalName] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [taxIdNumber, setTaxIdNumber] = useState('');
  const [footerText, setFooterText] = useState('');
  const [paymentTermsText, setPaymentTermsText] = useState('');
  const [locale, setLocale] = useState<'en' | 'ru'>('en');
  const [legalForm, setLegalForm] = useState<'IP' | 'OOO'>('OOO');
  const [kpp, setKpp] = useState('');
  const [address, setAddress] = useState('');
  const [bankName, setBankName] = useState('');
  const [bik, setBik] = useState('');
  const [settlementAccount, setSettlementAccount] = useState('');
  const [correspondentAccount, setCorrespondentAccount] = useState('');
  const [signerPosition, setSignerPosition] = useState('');
  const [signerName, setSignerName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);

  async function submit(): Promise<void> {
    setSaving(true);
    setSaveError(null);
    try {
      const ru = locale === 'ru';
      await endpoints.saveInvoiceTemplate({
        legalEntityId,
        sellerLegalName,
        registrationNumber: registrationNumber || undefined,
        taxIdNumber: taxIdNumber || undefined,
        footerText: footerText || undefined,
        paymentTermsText: paymentTermsText || undefined,
        ...(ru
          ? {
              locale: 'ru' as const,
              registeredAddress: address ? { address } : undefined,
              bankDetails: { ...(bankName ? { bankName } : {}), ...(bik ? { bik } : {}), ...(settlementAccount ? { settlementAccount } : {}), ...(correspondentAccount ? { correspondentAccount } : {}) },
              ruDetails: { legalForm, ...(legalForm === 'OOO' && kpp ? { kpp } : {}), ...(signerPosition ? { signerPosition } : {}), ...(signerName ? { signerName } : {}) },
            }
          : {}),
      });
      result.reload();
    } catch (err) {
      setSaveError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card">
      <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('billing.invoiceLegalTemplate')}</h2>
      <p className="sub">{t('billing.invoiceLegalTemplateHint')}</p>
      {result.status === 'loading' && <LoadingState />}
      {result.status === 'error' && <ErrorBanner error={result.error} onRetry={result.reload} />}
      {result.status === 'success' && (
        <>
          {result.data.active ? (
            <dl className="kv">
              <dt>{t('billing.sellerLegalName')}</dt>
              <dd>{String(result.data.active['seller_legal_name'])}</dd>
              <dt>{t('billing.registrationNumber')}</dt>
              <dd>{String(result.data.active['registration_number'] ?? '—')}</dd>
              <dt>{t('billing.taxVatId')}</dt>
              <dd>{String(result.data.active['tax_id_number'] ?? '—')}</dd>
            </dl>
          ) : (
            <EmptyState title={t('billing.noInvoiceTemplateOnFile')} />
          )}
          {!result.data.readiness.configured && (
            <p className="sub">
              {t('billing.stillMissing', { fields: result.data.readiness.missingFields.join(', ') })}
            </p>
          )}
        </>
      )}

      {hasPermission('BILLING.WRITE') && (
        <div style={{ marginTop: 16 }}>
          <h3 style={{ fontSize: 13 }}>{t('billing.saveNewVersion')}</h3>
          <div className="field-row">
            <label htmlFor="tpl-name">{t('billing.sellerLegalName')}</label>
            <input id="tpl-name" value={sellerLegalName} onChange={(e) => setSellerLegalName(e.target.value)} />
          </div>
          <div className="field-row">
            <label htmlFor="tpl-reg">{t('billing.registrationNumber')}</label>
            <input id="tpl-reg" value={registrationNumber} onChange={(e) => setRegistrationNumber(e.target.value)} />
          </div>
          <div className="field-row">
            <label htmlFor="tpl-tax-id">{t('billing.taxVatId')}</label>
            <input id="tpl-tax-id" value={taxIdNumber} onChange={(e) => setTaxIdNumber(e.target.value)} />
          </div>
          <div className="field-row">
            <label htmlFor="tpl-locale">{t('billing.pdfLanguage')}</label>
            <select id="tpl-locale" value={locale} onChange={(e) => setLocale(e.target.value === 'ru' ? 'ru' : 'en')}>
              <option value="en">{t('billing.pdfLanguageEn')}</option>
              <option value="ru">{t('billing.pdfLanguageRu')}</option>
            </select>
          </div>
          {locale === 'ru' && (
            <>
              <div className="field-row">
                <label htmlFor="tpl-form">{t('billing.legalForm')}</label>
                <select id="tpl-form" value={legalForm} onChange={(e) => setLegalForm(e.target.value === 'IP' ? 'IP' : 'OOO')}>
                  <option value="OOO">{t('billing.legalFormOoo')}</option>
                  <option value="IP">{t('billing.legalFormIp')}</option>
                </select>
              </div>
              {legalForm === 'OOO' && (
                <div className="field-row">
                  <label htmlFor="tpl-kpp">{t('billing.kpp')}</label>
                  <input id="tpl-kpp" inputMode="numeric" value={kpp} onChange={(e) => setKpp(e.target.value)} />
                </div>
              )}
              <div className="field-row">
                <label htmlFor="tpl-addr">{t('billing.ruAddress')}</label>
                <input id="tpl-addr" value={address} onChange={(e) => setAddress(e.target.value)} />
              </div>
              <div className="field-row">
                <label htmlFor="tpl-bank">{t('billing.bankName')}</label>
                <input id="tpl-bank" value={bankName} onChange={(e) => setBankName(e.target.value)} />
              </div>
              <div className="field-row">
                <label htmlFor="tpl-bik">{t('billing.bik')}</label>
                <input id="tpl-bik" inputMode="numeric" value={bik} onChange={(e) => setBik(e.target.value)} />
              </div>
              <div className="field-row">
                <label htmlFor="tpl-acc">{t('billing.settlementAccount')}</label>
                <input id="tpl-acc" inputMode="numeric" value={settlementAccount} onChange={(e) => setSettlementAccount(e.target.value)} />
              </div>
              <div className="field-row">
                <label htmlFor="tpl-corr">{t('billing.correspondentAccount')}</label>
                <input id="tpl-corr" inputMode="numeric" value={correspondentAccount} onChange={(e) => setCorrespondentAccount(e.target.value)} />
              </div>
              <div className="field-row">
                <label htmlFor="tpl-sigpos">{t('billing.signerPosition')}</label>
                <input id="tpl-sigpos" value={signerPosition} onChange={(e) => setSignerPosition(e.target.value)} />
              </div>
              <div className="field-row">
                <label htmlFor="tpl-signame">{t('billing.signerName')}</label>
                <input id="tpl-signame" value={signerName} onChange={(e) => setSignerName(e.target.value)} />
              </div>
            </>
          )}
          <div className="field-row">
            <label htmlFor="tpl-footer">{t('billing.footerText')}</label>
            <input id="tpl-footer" value={footerText} onChange={(e) => setFooterText(e.target.value)} />
          </div>
          <div className="field-row">
            <label htmlFor="tpl-terms">{t('billing.paymentTerms')}</label>
            <input id="tpl-terms" value={paymentTermsText} onChange={(e) => setPaymentTermsText(e.target.value)} placeholder={t('billing.paymentTermsPlaceholder')} />
          </div>
          {Boolean(saveError) && <ErrorBanner error={saveError} />}
          <div className="form-actions">
            <button type="button" className="primary" disabled={saving || !sellerLegalName} onClick={() => void submit()}>
              {saving ? t('common.saving') : t('billing.saveNewTemplateVersion')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ========================================================================== */
/*  Data Export (ADR-078 Part D)                                             */
/* ========================================================================== */

function DataExportTab(): JSX.Element {
  const result = useApi(() => endpoints.exports(), []);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<unknown>(null);
  const { t, locale } = useLanguage();

  async function generate(): Promise<void> {
    setGenerating(true);
    setGenError(null);
    try {
      await endpoints.generateExport();
      result.reload();
    } catch (err) {
      setGenError(err);
    } finally {
      setGenerating(false);
    }
  }

  async function download(id: string): Promise<void> {
    const artifact = await endpoints.exportArtifact(id);
    const bytes = Uint8Array.from(atob(artifact.bytesBase64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `export-${id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('billing.fullOrgExport')}</h2>
        <p className="sub">{t('billing.fullOrgExportHint')}</p>
        {Boolean(genError) && <ErrorBanner error={genError} />}
        <div className="form-actions">
          <button type="button" className="primary" disabled={generating} onClick={() => void generate()}>
            {generating ? t('billing.generating') : t('billing.generateNewExport')}
          </button>
        </div>
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('billing.previousExports')}</h2>
        {result.status === 'loading' && <LoadingState />}
        {result.status === 'error' && <ErrorBanner error={result.error} onRetry={result.reload} />}
        {result.status === 'success' &&
          (result.data.exports.length === 0 ? (
            <EmptyState title={t('billing.noExportGenerated')} />
          ) : (
            <div className="table-scroll"><table>
              <thead>
                <tr>
                  <th>{t('billing.colGenerated')}</th>
                  <th>{t('billing.colSize')}</th>
                  <th>{t('common.status')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {result.data.exports.map((row) => {
                  const r = row as Record<string, unknown>;
                  const id = String(r.id);
                  return (
                    <tr key={id}>
                      <td>{formatDateTime(String(r.created_at), locale)}</td>
                      <td>{r.size_bytes ? t('billing.sizeKb', { kb: formatNumber(Number(r.size_bytes) / 1024, locale, { maximumFractionDigits: 1 }) }) : '—'}</td>
                      <td>
                        <StatusBadge value={String(r.status)} />
                      </td>
                      <td>
                        <button type="button" onClick={() => void download(id)}>
                          {t('billing.download')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          ))}
      </div>
    </div>
  );
}
